import { Component } from 'react';
import classNames from 'classnames/bind';
import GeneralIcon, { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import ButtonBase from 'corecomponents/ButtonBase/ButtonBase';
import PropTypes from 'prop-types';
import FormatBottomBar from './FormatBottomBar/FormatBottomBar';
import ModalButton from './ModalButton';
import styles from './ModalLayout.module.css';

const cx = classNames.bind(styles);

/** @typedef {import('react').ReactNode} ReactNode */

/**
 * Every static below carries a JSDoc `@param` rather than being rewritten.
 *
 * In a `.js` file a destructured parameter with no default is inferred as a
 * **required** prop, so `<ModalLayout.Section className="…">` was an error for
 * not also passing `marginXl` — and each of these is used that way in a dozen
 * places. The annotations only record which props are optional and what they
 * hold; the JS is untouched, so webpack's output is identical.
 */
export default class ModalLayout extends Component {
  // To-Do: Remove this. Use closeButton flag on Modal component
  // to display the correct close button
  /** @param {{ handleClose?: () => void }} props */
  static Header = ({ handleClose }) => (
    <div className={cx('header')}>
      <ButtonBase onClick={handleClose}>
        <GeneralIcon icon={GENERAL_ICONS.CLOSE} />
      </ButtonBase>
    </div>
  );

  /**
   * @param {{ children?: ReactNode, label?: ReactNode, className?: string }} props
   */
  static PreTitle = ({ children, label, className }) => (
    <div className={cx('pre-title', className)}>{children || label}</div>
  );

  /**
   * @param {{ children?: ReactNode, label?: ReactNode, noMarginTop?: boolean,
   *   className?: string, noPadding?: boolean }} props
   */
  static Title = ({ children, label, noMarginTop, className, noPadding }) => (
    <div className={cx('title', className, { noMarginTop }, { noPadding })}>
      {children || label}
    </div>
  );

  // Modal level error message
  /** @param {{ children?: ReactNode, label?: ReactNode }} props */
  static Error = ({ children, label }) => (
    <div className={cx('error-message')}>{children || label}</div>
  );

  /**
   * @param {{ className?: string, children?: ReactNode, noPaddingBottom?: boolean,
   *   withMarginTop?: boolean, label?: ReactNode, whiteSpaceNormal?: boolean,
   *   noPadding?: boolean }} props
   */
  static SubTitle = ({
    className,
    children,
    noPaddingBottom,
    withMarginTop,
    label,
    whiteSpaceNormal,
    noPadding,
  }) => (
    <div
      className={cx(
        'sub-title',
        className,
        { noPaddingBottom },
        { withMarginTop },
        { whiteSpaceNormal },
        { noPadding },
      )}
    >
      {children || label}
    </div>
  );

  // Used to wrap a form
  /**
   * @param {{ children?: ReactNode, noMargin?: boolean, className?: string }} props
   */
  static Content = ({ children, noMargin, className }) => (
    <div className={cx('content', { noMargin }, className)}>{children}</div>
  );

  // Used to dictate sections of the form.
  /**
   * @param {{ children?: ReactNode, className?: string, marginXl?: boolean }} props
   */
  static Section = ({ children, className, marginXl }) => (
    <div className={cx('section', className, { marginXl })}>{children}</div>
  );

  /**
   * @param {{ children?: ReactNode, mobileSupportInput?: boolean,
   *   noMargin?: boolean, label?: ReactNode }} props
   */
  static SectionTitle = ({ children, mobileSupportInput, noMargin, label }) => (
    <div className={cx('section-title', { mobileSupportInput }, { noMargin })}>
      {children || label}
    </div>
  );

  static Button = (props) => <ModalButton {...props} />;

  /**
   * @param {{ children?: ReactNode,
   *   style?: import('react').CSSProperties }} props
   */
  static FullScreen = ({ children, style }) => (
    <div className={cx('full-screen')} style={style}>
      {children}
    </div>
  );

  /**
   * @param {{ ctaProps?: any, noButton?: boolean, fixedBottom?: boolean,
   *   cancelRequest?: any, leftSideNode?: ReactNode, rightNode?: ReactNode,
   *   removeMarginTopAndBorderBottomBar?: boolean,
   *   noMarginOrPaddingOnMobileForButton?: boolean, nextButtonWrapperProps?: any,
   *   reverseOrderOnMobile?: boolean, className?: string, makeNode?: any,
   *   noPadding?: boolean }} props
   */
  static BottomBar = ({
    ctaProps,
    noButton,
    fixedBottom,
    cancelRequest,
    leftSideNode,
    rightNode,
    removeMarginTopAndBorderBottomBar,
    noMarginOrPaddingOnMobileForButton,
    nextButtonWrapperProps,
    reverseOrderOnMobile,
    className = '',
    makeNode,
    noPadding,
  }) => (
    <>
      {!fixedBottom ? (
        <div
          className={cx(
            'bottom-bar',
            { noButton, noPadding },
            'no-bottom-margin',
            { reverseOrderOnMobile },
          )}
        >
          <FormatBottomBar
            noButton={noButton}
            cancelRequest={cancelRequest}
            ctaProps={ctaProps}
            rightNode={rightNode}
            leftSideNode={leftSideNode}
            nextButtonWrapperProps={nextButtonWrapperProps}
          />
        </div>
      ) : (
        <div
          className={cx(
            'bottom-bar',
            { removeMarginTopAndBorderBottomBar },
            { fixedBottom },
            { noButton },
            { reverseOrderOnMobile },
            { noPadding },
            className,
          )}
        >
          <FormatBottomBar
            noButton={noButton}
            cancelRequest={cancelRequest}
            ctaProps={ctaProps}
            rightNode={rightNode}
            leftSideNode={leftSideNode}
            noMarginOrPaddingOnMobileForButton={
              noMarginOrPaddingOnMobileForButton
            }
            nextButtonWrapperProps={nextButtonWrapperProps}
            makeNode={makeNode}
          />
        </div>
      )}
      <div className={cx('spacing-on-mobile-buttons')} />
    </>
  );

  // This should pass in the route to the previous page
  // and the Previous Step Icon should be here

  static propTypes = {
    children: PropTypes.node.isRequired,
    // Not sure if this should be here.
  };

  render() {
    const { children, className } = this.props;
    return <div className={cx('modal-layout', className)}>{children}</div>;
  }
}

ModalLayout.propTypes = {
  className: PropTypes.string,
};
